<template>
  <div class="pgm-notice">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <h1 class="page-title">
        <v-icon class="mr-2">mdi-bullhorn</v-icon>
        공지사항
      </h1>
      <v-btn color="white" variant="flat" @click="goToWrite">
        <v-icon start>mdi-plus</v-icon>
        글쓰기
      </v-btn>
    </div>

    <!-- 검색 -->
    <v-card class="search-card" elevation="1">
      <v-card-text class="pa-3">
        <v-row align="center" dense>
          <v-col cols="8" sm="6" md="4">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              placeholder="검색"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="4" sm="3" md="2">
            <v-btn 
              color="primary" 
              variant="tonal"
              @click="loadNotices"
              :loading="loading"
              block
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 공지사항 목록 -->
    <v-card class="notice-list-card" elevation="1">
      <!-- 로딩 -->
      <div v-if="loading" class="pa-4">
        <v-skeleton-loader type="list-item-three-line@5"></v-skeleton-loader>
      </div>

      <!-- 목록 -->
      <div v-else-if="filteredNotices.length > 0" class="notice-list">
        <div 
          v-for="notice in filteredNotices" 
          :key="notice.nc_idx" 
          class="notice-list-item"
          @click="goToDetail(notice)"
        >
          <v-chip :color="getCategoryColor(notice.nc_category)" size="x-small" variant="flat" class="mr-2">
            {{ getCategoryText(notice.nc_category) }}
          </v-chip>
          <span class="item-title">
            {{ notice.nc_title }}
            <v-icon v-if="notice.file_count > 0" size="12" color="grey" class="ml-1">mdi-attachment</v-icon>
            <span v-if="isNew(notice.nc_wdate)" class="new-badge">N</span>
          </span>
          <span class="item-author">{{ notice.nc_type === 'ADMIN' ? '관리자' : notice.nc_sisul_name || '-' }}</span>
          <span class="item-date">{{ formatListDate(notice.nc_wdate) }}</span>
        </div>
      </div>

      <!-- 데이터 없음 -->
      <div v-else class="no-data-message">
        <v-icon size="48" color="grey-lighten-1">mdi-bulletin-board</v-icon>
        <p>공지사항이 없습니다</p>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: 'NOTICE' | 'UPDATE' | 'EVENT' | 'URGENT';
  nc_sisul_code: string | null;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_hit: number;
  nc_wdate: string;
  file_count: number;
}

const router = useRouter();
const loading = ref(false);
const notices = ref<Notice[]>([]);
const searchQuery = ref('');

// 필터링된 공지사항
const filteredNotices = computed(() => {
  if (!searchQuery.value) return notices.value;
  
  const query = searchQuery.value.toLowerCase();
  return notices.value.filter(n => 
    n.nc_title?.toLowerCase().includes(query) ||
    n.nc_writer_name?.toLowerCase().includes(query)
  );
});

// 공지사항 목록 로드
const loadNotices = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page: 1, limit: 50 },
      withCredentials: true
    });
    
    if (response.data.success) {
      notices.value = response.data.notices;
    }
  } catch (error) {
    console.error('공지사항 목록 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

// 상세 페이지로 이동
const goToDetail = (notice: Notice) => {
  router.push(`/pgm/notice/${notice.nc_idx}`);
};

// 글쓰기 페이지로 이동
const goToWrite = () => {
  router.push('/pgm/notice/write');
};

// 유틸리티 함수
const formatDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  const today = new Date();
  
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  }
  
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
};

const formatListDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
};

const isNew = (date: string) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (now.getTime() - d.getTime()) < 3 * 24 * 60 * 60 * 1000;
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'NOTICE': return 'primary';
    case 'UPDATE': return 'info';
    case 'EVENT': return 'success';
    case 'URGENT': return 'error';
    default: return 'grey';
  }
};

const getCategoryText = (category: string) => {
  switch (category) {
    case 'NOTICE': return '공지';
    case 'UPDATE': return '업데이트';
    case 'EVENT': return '이벤트';
    case 'URGENT': return '긴급';
    default: return category;
  }
};

onMounted(() => {
  loadNotices();
});
</script>

<style lang="scss" scoped>
.pgm-notice {
  padding: 16px;
  max-width: 800px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border-radius: 12px;
  
  .page-title {
    font-size: 20px;
    font-weight: 600;
    color: white;
    display: flex;
    align-items: center;
    margin: 0;
  }
}

.search-card {
  margin-bottom: 12px;
  border-radius: 8px;
}

.notice-list-card {
  border-radius: 8px;
  overflow: hidden;
}

.notice-list {
  padding: 0;
}

.notice-list-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #fafafa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  .item-title {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    
    .new-badge {
      display: inline-block;
      background: #d32f2f;
      color: white;
      font-size: 10px;
      font-weight: 600;
      padding: 1px 4px;
      border-radius: 3px;
      margin-left: 6px;
    }
  }
  
  .item-author {
    font-size: 12px;
    color: #666;
    margin-left: 12px;
    white-space: nowrap;
  }
  
  .item-date {
    font-size: 12px;
    color: #999;
    margin-left: 12px;
    white-space: nowrap;
  }
}

.no-data-message {
  padding: 60px 20px;
  text-align: center;
  color: #999;
  
  p {
    margin-top: 12px;
    font-size: 14px;
  }
}
</style>

